// src/pages/Event/EventPage.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SEO from "../../components/SEO";
import GroupByRole from "./_components/GroupByRole";
import GroupPerformersByRole from "./_components/GroupPerformersByRole";
import { getEventImage } from "./_components/utils";
import InquiryForm from "./_components/InquiryForm";
import Breadcrumb from "../../components/Breadcrumb";
import GroupByNoRole from "./_components/GroupByNoRole";
import { useTranslation } from "react-i18next";

// Normalize any URL to absolute https://www.operatoday.org
function normalizeUrl(url) {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `https://www.operatoday.org${url}`;
  return `https://www.operatoday.org/${url}`;
}

function EventPage() {
  const { operaHouseName, eventSlug } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [hotels, setHotels] = useState([]);
  const { t } = useTranslation();

  const formatVenueName = (name) => {
    if (!name) return null;
    return name.split(" ").map((word, wordIndex) => (
      <span key={wordIndex}>
        <span>{word.charAt(0).toUpperCase()}</span>
        <span>{word.slice(1)}</span>
        {wordIndex < name.split(" ").length - 1 && " "}
      </span>
    ));
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(
          `/api/events/slug/${operaHouseName}/${eventSlug}`
        );
        setEvent(res.data);
        const hotelsResponse = await axios.get(
          `/api/hotels/venues/${res.data.venue._id}/hotels`
        );
        setHotels(hotelsResponse.data);
      } catch (err) {
        setError(t("EventPage.errorLoadingEvent"));
      }
    };
    fetchEvent();
  }, [operaHouseName, eventSlug]);

  const handleImageError = (e) => {
    e.target.src = "/images/default-event.jpg";
  };

  if (!event)
    return (
      <div className="text-center text-black">
        {error || t("EventPage.loading")}
      </div>
    );

  // ----- original helpers -----
  const formatDates = (dates) => {
    if (!dates?.length) return "";
    const groupedDates = {};
    dates.forEach((date) => {
      const dateObj = new Date(date.date);
      const monthYear = dateObj.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      groupedDates[monthYear] = groupedDates[monthYear] || [];
      groupedDates[monthYear].push(dateObj.getDate());
    });
    return Object.entries(groupedDates)
      .map(
        ([monthYear, days]) =>
          `${days.sort((a, b) => a - b).join(", ")} ${
            monthYear.split(" ")[0]
          } ${monthYear.split(" ")[1]}`
      )
      .join(", ");
  };

  const formattedDates = formatDates(event.date_times);

  const composers = event.composers
    .map((composer) => ({
      name: composer.name,
      slug: composer.name.toLowerCase().replace(/\s+/g, "-"),
    }))
    .filter(Boolean);

  const company = [];
  if (event.ensamble?.Orchester)
    company.push(`Orchester ${event.ensamble.Orchester}`);
  if (event.ensamble?.Chor) company.push(`Chor ${event.ensamble.Chor}`);

  const performers = event.cast || [];

  const countryCodes = {
    France: "FR",
    Germany: "DE",
    Italy: "IT",
    Austria: "AT",
    "United States": "US",
    "United Kingdom": "GB",
  };
  const countryCode =
    countryCodes[event.venue?.country] || event.venue?.country;

  const earliestDate =
    event.date_times?.length > 0
      ? new Date(
          Math.min(...event.date_times.map((d) => new Date(d.date)))
        ).toISOString()
      : "";

  // ----- ONLY SEO-related additions below -----

  // Canonical path for this page (matches your actual route)
  const canonicalPath = `/${encodeURIComponent(
    operaHouseName
  )}/${encodeURIComponent(eventSlug)}`;
  const canonicalUrl = normalizeUrl(canonicalPath);

  // Compute image candidates for JSON-LD (no UI change)
  const rawEventImage = getEventImage(event, "event") || event.image;
  const absEventImage = normalizeUrl(rawEventImage);
  const absVenueCover = normalizeUrl(event.venue?.coverImage);
  const imageArray = [absEventImage, absVenueCover].filter(
    (v, i, arr) => v && arr.indexOf(v) === i
  );

  // Offer: ONLY use basePrice
  const numericBase = (() => {
    const n =
      typeof event.basePrice === "string"
        ? Number(event.basePrice)
        : event.basePrice;
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  // JSON-LD schema (MusicEvent)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "@id": `${canonicalUrl}#event`,
    url: canonicalUrl,
    name: event.title || "Untitled Event",
    startDate: earliestDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    ...(imageArray.length ? { image: imageArray } : {}),
    location: {
      "@type": "PerformingArtsTheater",
      name: event.venue?.name || "Unknown Venue",
      address: {
        "@type": "PostalAddress",
        streetAddress:
          event.venue?.streetAddress || event.venue?.street || undefined,
        addressLocality: event.venue?.city || undefined,
        addressCountry: countryCode || undefined,
      },
    },
    workPerformed: {
      "@type": "CreativeWork",
      name: event.title || "Untitled Event",
      ...(composers[0]
        ? { creator: { "@type": "Person", name: composers[0].name } }
        : {}),
    },
    performer: performers
      .filter((p) => p?.name)
      .map((p) => ({
        "@type": "Role",
        roleName: p.role || "Performer",
        actor: {
          "@type": "Person",
          name: p.name,
        },
      })),
    ...(numericBase != null
      ? {
          offers: {
            "@type": "Offer",
            url: canonicalUrl,
            price: String(numericBase),
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            ...(earliestDate ? { validFrom: earliestDate } : {}),
          },
        }
      : {}),
  };

  // Prune empty address fields to avoid validation noise
  if (!schemaData.location.address.streetAddress)
    delete schemaData.location.address.streetAddress;
  if (!schemaData.location.address.addressLocality)
    delete schemaData.location.address.addressLocality;
  if (!schemaData.location.address.addressCountry)
    delete schemaData.location.address.addressCountry;
  if (!schemaData.workPerformed.creator)
    delete schemaData.workPerformed.creator;

  // ----- UI remains unchanged below -----

  const imageUrl = getEventImage(event, "event");

  // SEO meta description
  const metaDescription = `${event.title} at ${
    event.venue?.name || "opera venue"
  } - ${formattedDates}. ${
    composers.length > 0 ? `Music by ${composers[0].name}. ` : ""
  }Book your opera experience today.`;

  return (
    <div className="bg-white">
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={`${event.title} at ${
          event.venue?.name || "Opera Venue"
        } | Opera Today`}
        description={metaDescription}
        canonical={canonicalUrl}
        image={absEventImage || absVenueCover}
        type="article"
        schema={schemaData}
      />

      {event.venue.coverImage ? (
        <div className="relative w-full h-[170px] md:h-[23vw] overflow-hidden">
          <img
            src={event.venue.coverImage}
            alt={event.title}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        </div>
      ) : (
        <div className="w-full bg-white flex items-center justify-center text-black text-base shadow-md mt-20 sm:mt-28"></div>
      )}

      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          {
            href: "/opera-houses-and-festivals",
            label: "Destinations",
          },
          {
            href: `/${operaHouseName}`,
            label: formatVenueName(operaHouseName),
          },
          {
            href: "#",
            label: event.title,
          },
        ]}
      />

      <main className="max-w-[1280px] mx-auto py-10 px-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Details */}
          <div className="w-full lg:w-[74%]">
            <div>
              <div className="w-full overflow-hidden">
                <div>
                  <h1 className="text-3xl text-opera-red mb-2 capitalize">
                    {event.title}
                  </h1>
                  <p className="text-lg text-black mb-4">{formattedDates}</p>
                </div>
                <div className="md:flex gap-10 items-start">
                  <div className="w-full md:w-[480px] mb-4">
                    <h2 className="text-xl text-opera-red font-cinzel-important mb-2">
                      {t("EventPage.performanceInformation")}
                    </h2>
                    {event.crews && <GroupByNoRole items={event.crews} />}
                    <p className="text-black">
                      <span className="font-lato-light-important text-base">
                        {t("EventPage.musicBy")}:
                      </span>
                      <span className="inline-block w-2"></span>
                      <span className="font-lato-light-important text-base">
                        {composers.length > 0
                          ? composers.map((c, i) => (
                              <span key={c.slug}>
                                {c.name}
                                {i < composers.length - 1 ? ", " : ""}
                              </span>
                            ))
                          : t("EventPage.notSpecified")}
                      </span>
                    </p>
                    {event.genre && (
                      <p className="text-black">
                        <span className="font-lato-light-important text-base">
                          {t("EventPage.musicalGenre")}:
                        </span>
                        <span className="inline-block w-2"></span>
                        <span className="font-lato-light-important text-base">
                          {event.genre.toLowerCase() === "dance"
                            ? "Ballet"
                            : event.genre.charAt(0).toUpperCase() +
                              event.genre.slice(1)}
                        </span>
                      </p>
                    )}
                    {event.crews && <GroupByRole items={event.crews} />}
                    {event.description && (
                      <p className="text-base text-black">
                        {event.description}
                      </p>
                    )}
                    {company.map((item, i) => {
                      const [label, ...valueParts] = item.split(" ");
                      const value = valueParts.join(" ");
                      return (
                        <p key={i} className="text-black">
                          <span className="font-lato-light-important text-base">
                            {label}:
                          </span>
                          <span className="inline-block w-2"></span>
                          <span className="font-lato-light-important text-base">
                            {value}
                          </span>
                        </p>
                      );
                    })}
                  </div>
                  <div className="w-full md:w-[480px] overflow-hidden mt-8 md:mt-0">
                    {performers.length > 0 && (
                      <div>
                        <h2 className="text-xl text-opera-red font-cinzel-important mb-2">
                          {t("EventPage.mainPerformers")}
                        </h2>
                        <GroupPerformersByRole performers={performers} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:gap-10">
              <div className="mt-4 pt-4 md:w-[480px]">
                <div className="text-xl text-opera-red font-cinzel font-medium bg-white w-full text-left flex justify-between items-center focus:outline-none active:outline-none">
                  {t("EventPage.included")}
                </div>
                <p className="text-base text-black mt-2">
                  {event.includedInPrice ? (
                    <>
                      <ul className="list-disc pl-6">
                        {event.includedInPrice
                          .split("-")
                          .map((item) => item.trim())
                          .filter(Boolean)
                          .map((item, idx) => (
                            <li key={idx} className="text-black">
                              {item}
                            </li>
                          ))}
                      </ul>
                      {event?.basePrice > 0 && (
                        <div>
                          <p className="mt-4 font-medium">
                            {t("EventPage.pricePerPerson")} €{event.basePrice}
                          </p>
                          <p>{t("EventPage.taxesFeesIncluded")}</p>
                        </div>
                      )}
                    </>
                  ) : event.venue.includedInPrice ? (
                    <>
                      <ul className="list-disc pl-6">
                        {event.venue.includedInPrice
                          .split("-")
                          .map((item) => item.trim())
                          .filter(Boolean)
                          .map((item, idx) => (
                            <li key={idx} className="text-black">
                              {item}
                            </li>
                          ))}
                      </ul>
                      {event?.basePrice > 0 && (
                        <div>
                          <p className="mt-4 font-medium">
                            {t("EventPage.pricePerPerson")} €{event.basePrice}
                          </p>
                          <p>{t("EventPage.taxesFeesIncluded")}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    "Details about included services will be provided soon."
                  )}
                </p>
              </div>

              <div className="mt-4 pt-4 md:w-[480px]">
                <div className="text-xl text-opera-red font-cinzel font-medium w-full text-left flex justify-between items-center focus:outline-none active:outline-none">
                  {t("EventPage.optionalExtras")}
                </div>
                <p className="text-base text-black mt-2">
                  {event.optionalExtras ? (
                    <ul className="list-disc pl-6">
                      {event.optionalExtras
                        .split("-")
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item, idx) => (
                          <li key={idx} className="text-black">
                            {item}
                          </li>
                        ))}
                    </ul>
                  ) : event.venue.optionalExtras ? (
                    <ul className="list-disc pl-6">
                      {event.venue.optionalExtras
                        .split("-")
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item, idx) => (
                          <li key={idx} className="text-black">
                            {item}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    t("EventPage.optionalExtrasDetails")
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Right column (unchanged UI) */}
          <div className="w-full lg:w-[26%] overflow-hidden mt-8 lg:mt-0 hidden lg:block">
            <div className="mb-4">
              <div className="flex flex-col items-start">
                <div style={{ display: "table", marginBottom: "4px" }}>
                  <div
                    style={{
                      display: "table-cell",
                      verticalAlign: "bottom",
                      paddingRight: "8px",
                    }}
                  >
                    <img
                      src="/images/venue.jpg"
                      alt="Venue"
                      width="50"
                      height="50"
                      style={{ display: "block" }}
                    />
                  </div>
                  <div
                    style={{ display: "table-cell", verticalAlign: "bottom" }}
                  >
                    <h2
                      className="text-xl capitalize"
                      style={{ margin: "0", padding: "0", lineHeight: "1.2" }}
                    >
                      {event.venue?.name || t("EventPage.unknownVenue")}
                    </h2>
                    <p
                      className="text-base text-black"
                      style={{ margin: "0", padding: "0", lineHeight: "1.2" }}
                    >
                      {event.venue?.streetAddress || ""}{" "}
                      {`${event.venue.postalCode ? "," : ""}${
                        event.venue.postalCode ?? ""
                      } ${event.venue.city || ""}`}
                      {countryCode ? `,  ${countryCode}` : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {imageUrl && (
              <img
                src={imageUrl}
                alt={event.title}
                className="w-full max-w-[260px] aspect-square object-cover border bg-white border-black-300 mt-9"
                onError={handleImageError}
              />
            )}
            {!imageUrl && (
              <div className="w-full max-w-[260px] aspect-square bg-white flex items-center justify-center text-black text-base shadow-md"></div>
            )}
          </div>
        </div>

        {/* Inquiry Form - Spanning 840px, aligned to the left */}
        <div className="mt-8 w-full lg:max-w-[1199px]">
          <InquiryForm event={event} hotels={hotels} setHotels={setHotels} />
        </div>
      </main>
    </div>
  );
}

export default EventPage;
